import { useSearchParams } from "react-router-dom";
import { GearSix, Buildings, Stack, IdentificationBadge, ShieldCheck, UsersThree, Clock, CalendarX, GlobeHemisphereWest, UserGear } from "@phosphor-icons/react";
import { GeneralSettings } from "../components/settings/GeneralSettings";
import { BranchesSettings } from "../components/settings/BranchesSettings";
import { DepartmentsSettings } from "../components/settings/DepartmentsSettings";
import { PositionsSettings } from "../components/settings/PositionsSettings";
import { RolesSettings } from "../components/settings/RolesSettings";
import { TeamsSettings } from "../components/settings/TeamsSettings";
import { WorkSchedulesSettings } from "../components/settings/WorkSchedulesSettings";
import { HolidaysSettings } from "../components/settings/HolidaysSettings";
import { TimeZonesSettings } from "../components/settings/TimeZonesSettings";
import { UserAccountsSettings } from "../components/settings/UserAccountsSettings";

// Grouped Information Architecture (Organization / Scheduling / Access & Security).
const GROUPS = [
  {
    label: "Organization",
    items: [
      { key: "general", label: "General", icon: GearSix, Comp: GeneralSettings },
      { key: "branches", label: "Branches", icon: Buildings, Comp: BranchesSettings },
      { key: "departments", label: "Departments", icon: Stack, Comp: DepartmentsSettings },
      { key: "positions", label: "Positions", icon: IdentificationBadge, Comp: PositionsSettings },
      { key: "teams", label: "Teams", icon: UsersThree, Comp: TeamsSettings },
    ],
  },
  {
    label: "Scheduling",
    items: [
      { key: "work-schedules", label: "Work Schedules", icon: Clock, Comp: WorkSchedulesSettings },
      { key: "holidays", label: "Holidays", icon: CalendarX, Comp: HolidaysSettings },
      { key: "time-zones", label: "Time Zones", icon: GlobeHemisphereWest, Comp: TimeZonesSettings },
    ],
  },
  {
    label: "Access & Security",
    items: [
      { key: "roles", label: "Roles", icon: ShieldCheck, Comp: RolesSettings },
      { key: "user-accounts", label: "User Accounts", icon: UserGear, Comp: UserAccountsSettings },
    ],
  },
];

const SECTIONS = GROUPS.flatMap((g) => g.items);

export default function Settings() {
  // Deep-linking: the active tab is driven by the URL (?tab=key), making each
  // Settings section shareable/bookmarkable. Backward compatible: missing/invalid
  // param falls back to "general".
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const active = SECTIONS.some((s) => s.key === tabParam) ? tabParam : "general";
  const ActiveComp = SECTIONS.find((s) => s.key === active)?.Comp || GeneralSettings;
  const setActive = (key) => setSearchParams({ tab: key }, { replace: true });

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="small-label">Configuration</div>
        <h1 className="font-display text-3xl font-semibold mt-1">Settings</h1>
        <p className="text-[#475569] text-sm mt-1">Configure the foundation of your organization.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Grouped sub-nav: horizontal scroll on mobile, vertical grouped on desktop */}
        <nav
          className="flex lg:flex-col gap-1 lg:gap-0 overflow-x-auto lg:w-60 shrink-0 -mx-1 px-1 lg:mx-0 lg:px-0"
          data-testid="settings-subnav"
        >
          {GROUPS.map((group) => (
            <div key={group.label} className="flex lg:flex-col gap-1 lg:gap-0.5 lg:mb-4">
              <div className="hidden lg:block small-label px-3 mb-1" data-testid={`settings-group-${group.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {group.label}
              </div>
              {group.items.map((s) => {
                const Icon = s.icon;
                const isActive = active === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActive(s.key)}
                    data-testid={`settings-tab-${s.key}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${
                      isActive ? "bg-[#0EA5E9] text-white" : "text-[#475569] hover:bg-[#F1F5F9]"
                    }`}
                  >
                    <Icon size={18} weight={isActive ? "fill" : "regular"} /> {s.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="flex-1 min-w-0 max-w-4xl">
          <ActiveComp />
        </div>
      </div>
    </div>
  );
}
